/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Activity } from 'types/entities/Activity'
import { Package } from 'types/entities/Package'
import { BookingFilter, BookingProcess } from 'types/app'
import { Company } from 'types/entities/Company'
import { PackagesCart } from 'types/entities/PackagesCart'
import { Cookies } from 'types/entities/Cookies'
import { Cookies_status } from 'types/enums/Cookies_status'
import { Payment } from 'types/entities/Payment'
import { Timeslot } from 'types/entities/Timeslot'
import { PackageCategory } from 'types/enums/PackageCategory'
import {
    PackagesItemByPerson,
    PackagesItemsResponse,
} from 'types/entities/PackagesItems'
import { RouteName } from 'types/enums/RouteName'
import {clampCreditQuantity, getSelectedBookingCredits, reconcileCreditItems} from 'utils/booking/creditItems'
import {isCreditPackageItem} from 'utils/offers/addItemToCalculationUtils'
import type {SelectedPackageCreditItem} from 'types/entities/PackagesItems'

export interface BookingNavigationStep {
    id: number
    label: string
}

export interface AllowedStep {
    id: number
    label: string
    routeName: RouteName
}

export const TIMESLOTS_ALLOWED_STEPS: AllowedStep[] = [
    { id: 1, label: 'Home', routeName: RouteName.Main },
    { id: 2, label: 'Options', routeName: RouteName.Options },
    { id: 3, label: 'Timeslots', routeName: RouteName.TimeslotsNew },
    { id: 4, label: 'Package', routeName: RouteName.Packages },
    { id: 5, label: 'Recapitulation', routeName: RouteName.Customer },
    { id: 6, label: 'Payment', routeName: RouteName.Paid },
]

export const TIMESLOTS_ALLOWED_STEPS_FOR_GIFT: AllowedStep[] = [
    { id: 1, label: 'Home', routeName: RouteName.Main },
    { id: 2, label: 'Gift voucher', routeName: RouteName.Gift },
    { id: 3, label: 'Checkout', routeName: RouteName.GiftCheckout },
]

export const TIMESLOTS_ALLOWED_STEPS_FOR_SUBSCRIPTION: AllowedStep[] = [
    { id: 1, label: 'Home', routeName: RouteName.Main },
    { id: 2, label: 'Subscription', routeName: RouteName.ThreeTwo },
    { id: 3, label: 'Checkout', routeName: RouteName.GiftCheckout },
]

export const TIMESLOTS_UPSELLS_ALLOWED_STEPS: AllowedStep[] = [
    { id: 1, label: 'Home', routeName: RouteName.Main },
    { id: 2, label: 'Options', routeName: RouteName.Options },
    { id: 3, label: 'Timeslots', routeName: RouteName.TimeslotsNew },
    { id: 4, label: 'Package', routeName: RouteName.Packages },
    { id: 5, label: 'Upsells', routeName: RouteName.UpsellsNew },
    { id: 6, label: 'Recapitulation', routeName: RouteName.Customer },
    { id: 7, label: 'Payment', routeName: RouteName.Paid },
]

const TIMESLOT_NOT_ALLOWED_STEPS = [
    { id: 1, label: 'Packages' },
    { id: 2, label: 'Customer' },
    { id: 3, label: 'Paid' },
]

export interface BookingNavigation {
    currentStep: BookingNavigationStep
    timeslotsAllowedSteps: BookingNavigationStep[]
    timeslotsGiftAllowedSteps: BookingNavigationStep[]
    timeslotsNotAllowedSteps: BookingNavigationStep[]
}

export interface BookingReducerState {
    activities: Activity[]
    packages: Package[]
    timeslotsAllowed: boolean
    timeslots: Timeslot[]
    bookingProcess: BookingProcess
    cookies: Cookies
    navigation: BookingNavigation
}

const getStandardDate = () => {
    const date = new Date()
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

const initBookingProcess: BookingProcess = {
    filter: {
        hour: 0,
        minute: 0,
        adults: 0,
        children: 0,
        date: getStandardDate(),
        activity_ids: [],
        groupType: [],
        hours_count: 0,
        category: PackageCategory.General,
    },
    package: null,
    upsell: [],
    selectedPackageCreditItems: [],
    customer: null,
    packageCart: null,
    voucher: null,
    payment: null,
    paymentAssert: null,
    notes: [],
    customTimeslots: [],
    possibleTimeslots: [],
    dateChosenByCustomer: false,
    emailVerified: false,
    emailLogin: false,
    selectedTimeslot: null,
    availablePackageItems: null,
    selectedPackageItems: [],
}

const initialState: BookingReducerState = {
    activities: [],
    packages: [],
    timeslotsAllowed: true,
    timeslots: [],
    bookingProcess: initBookingProcess,
    cookies: {
        tech_cookies: true,
        analytics_cookies: false,
        advert_cookies: false,
        status: Cookies_status.NULL,
    },
    navigation: {
        currentStep: TIMESLOTS_ALLOWED_STEPS[0],
        timeslotsAllowedSteps: TIMESLOTS_ALLOWED_STEPS,
        timeslotsGiftAllowedSteps: TIMESLOTS_ALLOWED_STEPS_FOR_GIFT,
        timeslotsNotAllowedSteps: TIMESLOT_NOT_ALLOWED_STEPS,
    },
}

const bookingSlice = createSlice({
    name: 'booking',
    initialState,
    reducers: {
        setDateChosenByCustomer(state, action: PayloadAction<boolean>) {
            state.bookingProcess.dateChosenByCustomer = action.payload
        },
        setEmailVerified(state, action: PayloadAction<boolean>) {
            state.bookingProcess.emailVerified = action.payload
        },
        setLoginVerified(state, action: PayloadAction<boolean>) {
            state.bookingProcess.emailLogin = action.payload
        },
        setPossibleTimeslots(state, action: PayloadAction<Timeslot[]>) {
            state.bookingProcess.possibleTimeslots = action.payload
        },
        setCustomTimeslot(state, action: PayloadAction<Timeslot>) {
            const index = state.bookingProcess.customTimeslots.findIndex(
                (timeslot) => timeslot.id === action.payload.id
            )
            if (index === -1) {
                state.bookingProcess.customTimeslots.push(action.payload)
            } else {
                state.bookingProcess.customTimeslots.splice(index, 1)
            }
        },
        resetCustomTimeslots(state) {
            state.bookingProcess.customTimeslots = []
        },
        setActivities(state, action: PayloadAction<Activity[]>) {
            state.activities = action.payload
        },
        setPackages(state, action: PayloadAction<Package[]>) {
            state.packages = action.payload
        },
        setAvailablePackagesItems(
            state,
            action: PayloadAction<PackagesItemsResponse>
        ) {
            state.bookingProcess.availablePackageItems = action.payload
            state.bookingProcess.selectedPackageCreditItems = reconcileCreditItems(
                getSelectedBookingCredits(state.bookingProcess), action.payload, state.bookingProcess.filter.adults, state.bookingProcess.filter.category
            )
            state.bookingProcess.upsell = state.bookingProcess.upsell.filter((item) => !isCreditPackageItem(item.packageItem))
        },
        clearAvailablePackagesItems(state) {
            state.bookingProcess.availablePackageItems = null
        },
        setSelectedPackageItems(
            state,
            action: PayloadAction<PackagesItemByPerson[]>
        ) {
            state.bookingProcess.selectedPackageItems = action.payload
        },
        setSelectedTimeslot(state, action: PayloadAction<Timeslot | null>) {
            state.bookingProcess.selectedTimeslot = action.payload
        },
        setSelectedPackageItem(
            state,
            action: PayloadAction<PackagesItemByPerson>
        ) {
            const index = state.bookingProcess.selectedPackageItems.findIndex(
                (item) => item.personIndex === action.payload.personIndex
            )
            if (index === -1) {
                state.bookingProcess.selectedPackageItems.push(action.payload)
            } else {
                state.bookingProcess.selectedPackageItems[index] =
                    action.payload
            }
        },
        resetSelectedPackageItems(state) {
            state.bookingProcess.selectedPackageItems = []
        },
        setSelectedPackageCreditItem(state, action: PayloadAction<SelectedPackageCreditItem>) {
            const current = getSelectedBookingCredits(state.bookingProcess)
                .filter((item) => item.packageItem.id !== action.payload.packageItem.id)
            state.bookingProcess.selectedPackageCreditItems = reconcileCreditItems(
                [...current, action.payload], state.bookingProcess.availablePackageItems, state.bookingProcess.filter.adults, state.bookingProcess.filter.category
            )
            state.bookingProcess.upsell = state.bookingProcess.upsell.filter((item) => !isCreditPackageItem(item.packageItem))
        },
        removeSelectedPackageCreditItem(state, action: PayloadAction<number>) {
            state.bookingProcess.selectedPackageCreditItems = getSelectedBookingCredits(state.bookingProcess)
                .filter((item) => item.packageItem.id !== action.payload)
            state.bookingProcess.upsell = state.bookingProcess.upsell.filter((item) => !isCreditPackageItem(item.packageItem))
        },
        setTimeslotsAllowed(state, action: PayloadAction<boolean>) {
            state.timeslotsAllowed = action.payload
        },
        setTimeslots(state, action: PayloadAction<Timeslot[]>) {
            state.timeslots = action.payload
        },
        setPackageCart(state, action: PayloadAction<PackagesCart | null>) {
            state.bookingProcess.packageCart = action.payload
        },
        setFilter(state, action: PayloadAction<BookingFilter>) {
            if (state.bookingProcess.filter.category !== action.payload.category) {
                state.bookingProcess.selectedPackageCreditItems = []
                state.bookingProcess.upsell = state.bookingProcess.upsell.filter((item) => !isCreditPackageItem(item.packageItem))
                state.bookingProcess.availablePackageItems = null
            } else {
                state.bookingProcess.selectedPackageCreditItems = getSelectedBookingCredits(state.bookingProcess)
                    .map((item) => ({...item, quantity: clampCreditQuantity(item.packageItem, item.quantity, action.payload.adults)}))
                    .filter((item) => item.quantity > 0)
                state.bookingProcess.upsell = state.bookingProcess.upsell.filter((item) => !isCreditPackageItem(item.packageItem))
            }
            state.bookingProcess.filter = {
                ...action.payload,
                adults: +action.payload.adults,
                children: +action.payload.children,
            }
        },
        clearFilterCategory(state) {
            state.bookingProcess.filter.category = null
            state.bookingProcess.selectedPackageItems = []
            state.bookingProcess.selectedPackageCreditItems = []
            state.bookingProcess.availablePackageItems = null
            state.bookingProcess.upsell = state.bookingProcess.upsell.filter(
                (item) => !isCreditPackageItem(item.packageItem)
            )
        },
        setFilterAdultsCount(state, action: PayloadAction<number>) {
            state.bookingProcess.filter.adults = +action.payload
            state.bookingProcess.selectedPackageCreditItems = getSelectedBookingCredits(state.bookingProcess)
                .map((item) => ({...item, quantity: clampCreditQuantity(item.packageItem, item.quantity, action.payload)}))
                .filter((item) => item.quantity > 0)
            state.bookingProcess.upsell = state.bookingProcess.upsell.filter((item) => !isCreditPackageItem(item.packageItem))
        },
        setFilterChildrenCount(state, action: PayloadAction<number>) {
            state.bookingProcess.filter.children = +action.payload
        },
        setPackage(state, action: PayloadAction<Package | null>) {
            state.bookingProcess.package = action.payload
        },
        resetReservationNote(state) {
            state.bookingProcess.notes = []
        },
        pushReservationNote(
            state,
            action: PayloadAction<BookingProcess['notes'][number]>
        ) {
            const index = state.bookingProcess.notes.findIndex(
                (note) => note.id === action.payload.id
            )
            if (index !== -1) {
                state.bookingProcess.notes[index] = action.payload
            } else {
                state.bookingProcess.notes.push(action.payload)
            }
        },
        setUpsell(
            state,
            action: PayloadAction<BookingProcess['upsell'][number]>
        ) {
            const index = state.bookingProcess.upsell.findIndex(
                (upsell) =>
                    upsell.packageItem.id === action.payload.packageItem.id
            )
            if (index !== -1) {
                state.bookingProcess.upsell[index] = action.payload
            }
        },
        pushUpsell(
            state,
            action: PayloadAction<BookingProcess['upsell'][number]>
        ) {
            const index = state.bookingProcess.upsell.findIndex(
                (upsell) =>
                    upsell.packageItem.id === action.payload.packageItem.id
            )
            if (index !== -1) {
                state.bookingProcess.upsell[index] = action.payload
            } else {
                state.bookingProcess.upsell.push(action.payload)
            }
        },
        removeUpsell(
            state,
            action: PayloadAction<BookingProcess['upsell'][number]>
        ) {
            state.bookingProcess.upsell = state.bookingProcess.upsell.filter(
                (upsell) =>
                    upsell.packageItem.id !== action.payload.packageItem.id
            )
        },
        setCustomer(state, action: PayloadAction<Company | null>) {
            state.bookingProcess.customer = action.payload
        },
        resetCustomer(state) {
            state.bookingProcess.customer = null
        },
        setCookies(state, action: PayloadAction<Cookies>) {
            state.cookies = action.payload
        },
        setPackagesCartPayment(state, action: PayloadAction<Payment | null>) {
            state.bookingProcess.payment = action.payload
        },
        setPackagesCartPaymentAssert(
            state,
            action: PayloadAction<BookingProcess['paymentAssert']>
        ) {
            state.bookingProcess.paymentAssert = action.payload
        },
        setCurrentNavigationStep(
            state,
            action: PayloadAction<BookingNavigationStep>
        ) {
            state.navigation.currentStep = action.payload
        },
        clearBookingProcessState(state) {
            state.bookingProcess = { ...initBookingProcess }
            state.timeslots = []
        },
        clearUpsells(state) {
            state.bookingProcess.upsell = []
        },
        resetState: () => initialState,
    },
})

export const {
    setDateChosenByCustomer,
    setEmailVerified,
    setLoginVerified,
    setPossibleTimeslots,
    setCustomTimeslot,
    resetCustomTimeslots,
    setActivities,
    setPackages,
    setAvailablePackagesItems,
    clearAvailablePackagesItems,
    setSelectedPackageItems,
    setSelectedTimeslot,
    setSelectedPackageItem,
    resetSelectedPackageItems,
    setSelectedPackageCreditItem,
    removeSelectedPackageCreditItem,
    setTimeslotsAllowed,
    setTimeslots,
    setPackageCart,
    setFilter,
    clearFilterCategory,
    setFilterAdultsCount,
    setFilterChildrenCount,
    setPackage,
    resetReservationNote,
    pushReservationNote,
    setUpsell,
    pushUpsell,
    removeUpsell,
    setCustomer,
    resetCustomer,
    setCookies,
    setPackagesCartPayment,
    setPackagesCartPaymentAssert,
    setCurrentNavigationStep,
    clearBookingProcessState,
    clearUpsells,
    resetState,
} = bookingSlice.actions

export default bookingSlice.reducer
